import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map, of, take } from 'rxjs';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { TOpUrlRouteContext } from 'Src/ng2/shared/services/user-and-route-context/user-and-route-context.service';
import { SetToggleProperty } from 'Src/ng2/store/actions/toggle-actions';
import { getCurrentUser } from 'Src/ng2/store/selectors/current-user-selectors';
import { getToggleEntities } from 'Src/ng2/store/selectors/toggles-selectors';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';
import { ENTITY_MODE } from 'Src/ng2/shared/typings/types/entity-mode.types';
import { TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';

// The network-tree counterpart to EntityResourceGuard's evaluation step: it evaluates the One Portal
// formula once for the network context being entered and persists the result, so every route below
// reads that evaluation instead of re-running the formula. Always returns true — this decides which
// portal a network route renders, never whether it can be accessed.
@Injectable({ providedIn: 'root' })
export class OpNetworkEvaluationGuard implements CanActivate {
  constructor (
    private store: Store<any>,
    private toggleService: ToggleService,
  ) {}

  canActivate (snapshot: ActivatedRouteSnapshot): Observable<boolean> {
    const entityType = this.getClusterType(snapshot);
    if (!entityType) {
      return of(true);
    }

    return combineLatest([
      this.store.select(getCurrentUser),
      this.store.select(getToggleEntities),
    ]).pipe(
      take(1),
      map(([user, toggles]) => {
        if (toggles?.[Toggles.EVALUATION_ONE_PORTAL_ENABLED] !== undefined) {
          return true;
        }

        const context: TOpUrlRouteContext = { entityType, entityMode: ENTITY_MODE.MULTI_ENTITY, user };
        this.persistPortalEnabledState(this.toggleService.onePortalEnabled(context));
        return true;
      }),
    );
  }

  // This runs from the `network` route's `data.guards`, so it gets that route's own snapshot while
  // `clusterType` lives on the `school/:clusterId` | `shelter/:clusterId` children — hence the walk
  // down the snapshot being activated.
  private getClusterType (snapshot: ActivatedRouteSnapshot): TOpEntityType | null {
    let current = snapshot;
    while (current) {
      const clusterType = current.data?.clusterType as TOpEntityType;
      if (clusterType) return clusterType;
      current = current.firstChild;
    }
    return null;
  }

  private persistPortalEnabledState (enabled: boolean): void {
    this.store.dispatch(new SetToggleProperty({
      [Toggles.EVALUATION_ONE_PORTAL_ENABLED]: enabled,
      [Toggles.TOGGLE_ONE_PORTAL]: enabled,
    }));
  }
}
